import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Skeleton,
  Typography,
  Box,
  TableContainer,
  Paper,
} from '@mui/material';
import ApprovalStatusChip from './ApprovalStatusChip';
import { getStatusToken } from '../utils/statusTokens';
import { HEAD_CELL_SX, ROW_CELL_SX, TABLE_CONTAINER_SX } from '../../../styles/tableStyles';

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (_e) {
    return '—';
  }
};

const formatDateOnly = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch (_e) {
    return '—';
  }
};


const ApprovalRow = ({ row, onActivate }) => {
  const effectiveStatus = row.is_expired ? 'expired' : row.status;
  const token = getStatusToken(effectiveStatus);
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onActivate(row.id);
    }
  };
  return (
    <TableRow
      hover
      tabIndex={0}
      role="link"
      aria-label={`Open approval for ${row.patient_name || 'patient'}`}
      onClick={() => onActivate(row.id)}
      onKeyDown={handleKeyDown}
      sx={{
        cursor: 'pointer',
        borderLeft: `3px solid ${token.rail}`,
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: -2,
        },
      }}
    >
      <TableCell sx={ROW_CELL_SX}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {row.patient_name || '—'}
        </Typography>
      </TableCell>
      <TableCell sx={ROW_CELL_SX}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {row.provider_name || '—'}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontFamily: 'Roboto Mono, ui-monospace, SFMono-Regular, monospace',
            color: 'text.secondary',
            display: 'block',
            mt: 0.25,
          }}
        >
          NPI {row.provider_npi || '----------'}
        </Typography>
      </TableCell>
      <TableCell sx={ROW_CELL_SX}>
        <ApprovalStatusChip status={effectiveStatus} />
      </TableCell>
      <TableCell sx={ROW_CELL_SX}>
        <Typography variant="caption" color="text.secondary">
          {formatDate(row.sent_at)}
        </Typography>
      </TableCell>
      <TableCell sx={ROW_CELL_SX}>
        <Typography variant="caption" color="text.secondary">
          {formatDateOnly(row.expires_at)}
        </Typography>
      </TableCell>
      <TableCell sx={{ ...ROW_CELL_SX, textAlign: 'right' }}>
        <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {row.document_count ?? 0}
        </Typography>
      </TableCell>
    </TableRow>
  );
};

const SkeletonRow = ({ keyIdx }) => (
  <TableRow key={`sk-${keyIdx}`} sx={{ borderLeft: '3px solid', borderColor: 'divider' }}>
    {[140, 180, 80, 130, 110, 30].map((w, i) => (
      <TableCell key={i} sx={ROW_CELL_SX}>
        <Skeleton variant="text" width={w} />
      </TableCell>
    ))}
  </TableRow>
);

const ApprovalListTable = ({ rows, loading, onActivate }) => (
  <TableContainer
    component={Paper}
    elevation={0}
    sx={TABLE_CONTAINER_SX}
  >
    <Table size="small" aria-label="Approvals list">
      <TableHead>
        <TableRow>
          <TableCell sx={{ ...HEAD_CELL_SX, borderLeft: '3px solid transparent' }}>Patient</TableCell>
          <TableCell sx={HEAD_CELL_SX}>Provider</TableCell>
          <TableCell sx={HEAD_CELL_SX}>Status</TableCell>
          <TableCell sx={HEAD_CELL_SX}>Sent</TableCell>
          <TableCell sx={HEAD_CELL_SX}>Expires</TableCell>
          <TableCell sx={{ ...HEAD_CELL_SX, textAlign: 'right' }}>Docs</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {loading && rows.length === 0 ? (
          Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} keyIdx={i} />)
        ) : (
          rows.map((row) => (
            <ApprovalRow key={row.id} row={row} onActivate={onActivate} />
          ))
        )}
      </TableBody>
    </Table>
    {!loading && rows.length === 0 && (
      <Box sx={{ p: 0 }} />
    )}
  </TableContainer>
);

export default ApprovalListTable;
